import { memo, useMemo } from "react";
import {
   Chip,
   Div,
   Divider,
   Form,
   getFormErrorObject,
   Icon,
   InputField,
   PageHeader,
   Text,
   useForm,
   useTheme,
   type AnyOtherString,
   type IconName,
} from "react-better-html";

import { useReduxAction, useReduxState } from "../../../utils/hooks";
import { passwordIsValid } from "../../../utils/functions";
import { useI18n } from "../../../utils/i18n";

import Section from "../../../components/structure/Section";

type ProviderData = {
   name: string;
   icon: IconName | AnyOtherString;
};

function SignInSecuritySection() {
   const theme = useTheme();
   const { t } = useI18n();

   const firebaseUser = useReduxState((state) => state.user.firebaseUser);

   const changePassword = useReduxAction((actions) => actions.user.changePassword);

   const changePasswordForm = useForm({
      defaultValues: {
         currentPassword: "",
         newPassword: "",
         confirmNewPassword: "",
      },
      requiredFields: ["currentPassword", "newPassword", "confirmNewPassword"],
      validate: (values) => {
         const errors = getFormErrorObject(values);

         if (!passwordIsValid(values.newPassword)) {
            errors.newPassword = t((l) => l.components.form.passwordRule);
         }
         if (values.newPassword !== values.confirmNewPassword) {
            errors.confirmNewPassword = t(
               (l) => l.pages.account.sections.signInSecurity.password.errors.passwordsDoNotMatch,
            );
         }

         return errors;
      },
      onSubmit: (values) => {
         changePassword({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            onSuccess: () => {
               changePasswordForm.reset();
            },
         });
      },
   });
   const setPasswordForm = useForm({
      defaultValues: {
         password: "",
         repeatPassword: "",
      },
      requiredFields: ["password", "repeatPassword"],
      validate: (values) => {
         const errors = getFormErrorObject(values);

         if (!passwordIsValid(values.password)) {
            errors.password = t((l) => l.components.form.passwordRule);
         }
         if (values.password !== values.repeatPassword) {
            errors.repeatPassword = t(
               (l) => l.pages.account.sections.signInSecurity.password.errors.passwordsDoNotMatch,
            );
         }

         return errors;
      },
      onSubmit: (values) => {
         changePassword({
            newPassword: values.password,
            onSuccess: () => {
               setPasswordForm.reset();
            },
         });
      },
   });

   const userHasPassword = useMemo<boolean>(
      () => firebaseUser?.providerData.find((provider) => provider.providerId === "password") !== undefined,
      [firebaseUser],
   );
   const providerIdToName = useMemo<Record<string, ProviderData>>(
      () => ({
         "google.com": {
            name: t((l) => l.pages.account.sections.signInSecurity.providers.google),
            icon: "googleG",
         },
         password: {
            name: t((l) => l.pages.account.sections.signInSecurity.providers.password),
            icon: "envelope",
         },
      }),
      [t],
   );

   return firebaseUser ? (
      <Section
         title={t((l) => l.pages.account.sections.signInSecurity.title)}
         description={t((l) => l.pages.account.sections.signInSecurity.description)}
      >
         <Div.column gap={theme.styles.space}>
            <Div.column gap={theme.styles.gap}>
               {firebaseUser.providerData.map((provider) =>
                  providerIdToName[provider.providerId] ? (
                     <Div.box
                        backgroundColor={theme.colors.backgroundBase}
                        paddingRight={theme.styles.gap}
                        key={provider.providerId}
                     >
                        <Div.row alignItems="center" gap={theme.styles.space}>
                           <Icon name={providerIdToName[provider.providerId]?.icon} color={theme.colors.primary} />
                           <Text flex={1} fontWeight={700}>
                              {providerIdToName[provider.providerId]?.name}
                           </Text>

                           <Chip.colored text={t((l) => l.common.enabled)} color={theme.colors.success} />
                        </Div.row>
                     </Div.box>
                  ) : (
                     <Div key={provider.providerId}>
                        <Text color={theme.colors.error}>
                           {t((l) => l.pages.account.sections.signInSecurity.providers.notSupported)}
                        </Text>
                     </Div>
                  ),
               )}
            </Div.column>

            <Divider.horizontal />

            <Div>
               <PageHeader
                  title={t(
                     (l) => l.pages.account.sections.signInSecurity.password[userHasPassword ? "change" : "set"].title,
                  )}
                  titleAs="h3"
                  marginBottom={theme.styles.space}
               />

               <Form
                  form={userHasPassword ? changePasswordForm : setPasswordForm}
                  gap={theme.styles.gap}
                  submitButtonText={t(
                     (l) =>
                        l.pages.account.sections.signInSecurity.password[userHasPassword ? "change" : "set"]
                           .submitButton,
                  )}
                  submitButtonLoaderName="user:changePassword"
               >
                  {userHasPassword ? (
                     <>
                        <InputField.password
                           label={t((l) => l.common.currentPassword)}
                           {...changePasswordForm.getInputFieldProps("currentPassword")}
                        />
                        <InputField.password
                           label={t((l) => l.common.newPassword)}
                           infoText={t((l) => l.components.form.passwordRule)}
                           {...changePasswordForm.getInputFieldProps("newPassword")}
                        />
                        <InputField.password
                           label={t((l) => l.common.confirmPassword)}
                           {...changePasswordForm.getInputFieldProps("confirmNewPassword")}
                        />
                     </>
                  ) : (
                     <>
                        <InputField.password
                           label={t((l) => l.common.password)}
                           infoText={t((l) => l.components.form.passwordRule)}
                           {...setPasswordForm.getInputFieldProps("password")}
                        />
                        <InputField.password
                           label={t((l) => l.common.confirmPassword)}
                           {...setPasswordForm.getInputFieldProps("repeatPassword")}
                        />
                     </>
                  )}
               </Form>
            </Div>
         </Div.column>
      </Section>
   ) : undefined;
}

export default memo(SignInSecuritySection);
